import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { InsightsUserHomeShellComponent } from 'src/app/home/insights-user-home/insights-user-home-shell.component';
import { SnackService } from 'src/app/services/snack.service';
import { catchAppError } from 'src/app/shared/utils';
import { AppActions, AppSelectors } from 'src/app/state/app';
import { WelcomeActions } from '.';
import { DashboardService, UserDashboardDto, UserDashboardService, UserRole, UserStateService } from '../../../services/api.services';
import { HomeActions } from '../../../state/home';
import { WelcomeShellComponent } from '../../containers/welcome-shell/welcome-shell.component';

@Injectable()
export class WelcomeEffects {
  private dialogRef: MatDialogRef<WelcomeShellComponent>;

  constructor(
    private store: Store,
    private actions$: Actions,
    private router: Router,
    private snackService: SnackService,
    private dialog: MatDialog,
    private dashboardService: DashboardService,
    private userDashboardService: UserDashboardService,
    private userStateService: UserStateService) { }


  toggleDialog$ = createEffect(() => this.actions$.pipe(
    ofType(WelcomeActions.setVisible),
    tap(({ visible }) => {
      if (visible) {
        this.dialogRef = this.dialog.open(WelcomeShellComponent, WelcomeShellComponent.dialogConfig);

        this.dialogRef.afterClosed().subscribe(() => {
          this.store.dispatch(WelcomeActions.setVisible({ visible: false }));
        });
      }
      else {
        if (this.dialogRef) {
          this.dialogRef.close();
          this.dialogRef = null;
        }
      }
    })),
    { dispatch: false }
  );

  showDialogAfterUserStateLoaded$ = createEffect(() => this.actions$.pipe(
    ofType(AppActions.loadSuccess),
    withLatestFrom(
      this.store.select(AppSelectors.getUser),
      this.store.select(AppSelectors.getUserState)
    ),
    // If we are on the home page, and user state is loaded, and firstHomeDashboardSelected == false
    filter(([_, user, userState]) => user.role >= UserRole.InsightsUser && userState.firstHomeDashboardSelected === false),
    map(({ }) => WelcomeActions.setVisible({ visible: true }))
  ));

  loadDashboards$ = createEffect(() => this.actions$.pipe(
    ofType(WelcomeActions.loadDashboards),
    switchMap(() => this.dashboardService.listWelcomeDashboards().pipe(
      map((dashboards) => WelcomeActions.loadDashboardsSuccess({ dashboards })),
      catchAppError(error => of(WelcomeActions.loadDashboardsFailure({ error })))))
  ));


  addDashboardToHome$ = createEffect(() => this.actions$.pipe(
    ofType(WelcomeActions.addDashboardToHome),
    switchMap(({ dashboardId }) => {
      let userDashboard: UserDashboardDto = {
        dashboardId,
        home: true
      };

      return this.userDashboardService.updateUserDashboard(userDashboard).pipe(
        map(() => WelcomeActions.addDashboardToHomeSuccess({ dashboardId })),
        catchAppError(error => of(WelcomeActions.addDashboardToHomeFailure({ error }))));
    })
  ));

  setFirstHomeDashboardSelectedAfterAddToHome$ = createEffect(() => this.actions$.pipe(
    ofType(WelcomeActions.addDashboardToHomeSuccess),
    map(({ dashboardId }) => WelcomeActions.setFirstHomeDashboardSelected({ dashboardId }))
  ));

  setFirstHomeDashboardSelected$ = createEffect(() => this.actions$.pipe(
    ofType(WelcomeActions.setFirstHomeDashboardSelected),
    map(({ dashboardId }) => AppActions.updateUserState({
      userState: {
        firstHomeDashboardSelected: true,
        lastViewedDashboardId: dashboardId
      },
      callbackAction: WelcomeActions.setFirstHomeDashboardSelectedSuccess({
        dashboardId
      })
    })
  )));

  afterSetFirstHomeDashboardSelected$ = createEffect(() => this.actions$.pipe(
    ofType(WelcomeActions.setFirstHomeDashboardSelectedSuccess),
    withLatestFrom(this.store.select(AppSelectors.pageComponent)),
    tap(([{ dashboardId }, pageComponent]) => {
      this.store.dispatch(WelcomeActions.setVisible({ visible: false }));

      if (pageComponent === InsightsUserHomeShellComponent.pageId) {
        this.store.dispatch(HomeActions.load({ dashboardId }));
      }
      else {
        this.router.navigate(['home']);
      }
    })
  ), { dispatch: false });


  adminHideDialog$ = createEffect(() => this.actions$.pipe(
    ofType(WelcomeActions.adminHideDialog),
    map(() => AppActions.updateUserState({
      userState: {
        firstHomeDashboardSelected: true,
      },
      callbackAction: WelcomeActions.setFirstHomeDashboardSelectedSuccess({
        dashboardId: null
      })
    })
  )));
}
